import { TimeoutError, timeoutFetch } from './redux/api/utils'
import database, {
  AVAILABLE_DTO_MAPPINGS,
  AVAILABLE_ENTITIES,
  Repository,
  fromDTO,
  toDTO,
  utils,
} from './database'
import { getLoggedUser, refreshToken } from './redux/auth/actions'
import { saveAs } from 'file-saver'

import {
  CHANGE_CURRENT_SYNC_VIEW,
  UPDATE_SYNC_PROGRESS,
  SYNC_STATUS,
} from './redux/sync/constants'
import { Q } from '@nozbe/watermelondb'
import compact from 'lodash/compact'
import each from 'lodash/each'
import find from 'lodash/find'
import flattenDeep from 'lodash/flattenDeep'
import isEmpty from 'lodash/isEmpty'
import isNumber from 'lodash/isNumber'
import uniq from 'lodash/uniq'
import hasInternet from '../src/utils/recognizeInternetConnection'
import { prepareMarkAsSynced } from '@nozbe/watermelondb/sync/impl/helpers'
import { recordError, recordEvent } from './observability/events'
import {
  classifyEventsResponse,
  summarizeRejections,
} from './observability/pushEventsResult'
import { store } from './store'
import uuid from 'react-uuid'

// ============================================================================
// MÓDULOS DE CONFIABILIDADE - Melhorias de Sincronização
// ============================================================================
import { syncLogger } from './sync/SyncLogger'
import { syncQueue } from './sync/SyncQueue'
import { tokenManager } from './sync/TokenManager'
import { checkpointManager, CheckpointPhase } from './sync/CheckpointManager'
import { circuitBreaker, CircuitOpenError } from './sync/CircuitBreaker'
import {
  pullChangesEnhanced,
  updateLastPulledAtSafely,
  updateFarmLastPulledAtSafely,
  pushEnhanced,
  getSyncStats,
  exportSyncLogs,
} from './sync/syncWorkerEnhanced'
import {
  calculateDynamicTimeout,
  getRetryTimeout,
  TIMEOUT_CONSTANTS,
} from './sync/DynamicTimeout'

// Flag para habilitar/desabilitar melhorias (permite rollback rápido)
const USE_ENHANCED_SYNC = true

// ============================================================================
// FUNÇÕES HELPER PARA STATUS DE SYNC
// ============================================================================

/**
 * Atualiza o status visual de sincronização
 * @param {number} status - Um dos valores de SYNC_STATUS
 */
function setSyncStatus(status) {
  store.dispatch({
    type: CHANGE_CURRENT_SYNC_VIEW,
    payload: status,
  })
}

/**
 * Atualiza o progresso detalhado de sincronização
 * @param {Object} progress - Informações de progresso
 */
function setSyncProgress(progress) {
  store.dispatch({
    type: UPDATE_SYNC_PROGRESS,
    payload: progress,
  })
}

/**
 * Helper combinado para setar status e progresso
 */
function updateSyncUI(status, progress = null) {
  setSyncStatus(status)
  if (progress) {
    setSyncProgress(progress)
  }
}

// const SYNC_PORTS = {
//   PULL: 93,
//   PUSH: 88,
//   AUTH: 81,
// }

// const SYNC_PULL_GROUP = 'sync-client'
const SYNC_PUSH_GROUP = 'sync'
const LOGIN_GROUP = 'login-register'

// const SYNC_TIMEOUT = 15000 //Incresed due to high response times by backend seen recently
const SYNC_TIMEOUT = 1000 * 60 * 5 //5 min //Incresed due to high response times by backend seen recently
// const EXTRA_TIMEOUT = 120000
const EXTRA_TIMEOUT = 1000 * 60 * 5 //5 min

const REACT_APP_BASE_URL_GATEWAY = process.env.REACT_APP_BASE_URL_GATEWAY
const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL
const REACT_APP_BASE_URL_PULL_GATEWAY_VERITY =
  process.env.REACT_APP_BASE_URL_PULL_GATEWAY_VERITY
const REACT_APP_BASE_URL_PUSH_GATEWAY_VERITY =
  process.env.REACT_APP_BASE_URL_PUSH_GATEWAY_VERITY
const REACT_APP_TOGGLE_GATEWAY_VERITY =
  process.env.REACT_APP_TOGGLE_GATEWAY_VERITY

const SYNC_URLS = {
  PULL: `${REACT_APP_BASE_URL}/api/Sync`,
  HALF_PULL: `${REACT_APP_BASE_URL}/half-sync`,
  PULL_GATEWAY_VERITY: `${REACT_APP_BASE_URL_PULL_GATEWAY_VERITY}/sincronizar`,
  PUSH: `${REACT_APP_BASE_URL_GATEWAY}/${SYNC_PUSH_GROUP}/api/api/Queue`,
  PUSH_GATEWAY_VERITY: `${REACT_APP_BASE_URL_PUSH_GATEWAY_VERITY}/enviar`,
  REFRESH_TOKEN: `${REACT_APP_BASE_URL_GATEWAY}/${LOGIN_GROUP}/api/Authentication/Login`,
  EVENTS: `${REACT_APP_BASE_URL_GATEWAY}/${SYNC_PUSH_GROUP}/api/api/Events`,
}

// Tamanho máximo do lote enviado em pushEvents.
const EVENTS_PUSH_BATCH_SIZE = 100

const SLEEP_TIME = 30000
const PREFIX = '[SYNC-WORKER]'
const LOG_TYPES = {
  LOG: '[LOG]',
  WARN: '[WARN]',
  ERROR: '[ERROR]',
}

const SYNC_OBJECT_TO_TABLE = {
  usuarios: 'users',
  tipoDominios: 'domain_types',
  valorDominios: 'domain_values',
  fazendas: 'farms',
  protocolos: 'protocols',
  lotes: 'batches',
  d0s: 'd0s',
  dns: 'dns',
  iatfs: 'iatfs',
  fazendaUsuarios: 'farms__rel__users',
  // touros: 'bulls',
  touros: 'bulls_new',
  partidas: 'partida',
  retiros: 'corrals',
  eventos: 'events',
}

const SYNC_OBJECT_TO_TABLE_ONLY_BATCH = {
  lotes: 'batches',
  d0s: 'd0s',
  dns: 'dns',
  iatfs: 'iatfs',
}

const toDTOMapping = (name) => {
  return name === 'farms__rel__users' ? 'FARM_USERS' : name.toUpperCase()
}

/*
const sendMessage = (message, type) => {
  postMessage(`${PREFIX}${type} ${message}`)
}
*/

const sendMessage = (message, type) => {
  const logMessage = `${PREFIX}${type} ${message}`
  switch (type) {
    case LOG_TYPES.WARN:
      console.warn(logMessage)
      break
    case LOG_TYPES.ERROR:
      console.error(logMessage)
      break
    case LOG_TYPES.LOG:
    default:
      if (process.env.NODE_ENV !== 'production') {
        console.log(logMessage)
      }
  }
}

const log = (message) => {
  sendMessage(message, LOG_TYPES.LOG)
}

const warn = (message) => {
  sendMessage(message, LOG_TYPES.WARN)
}

const warnPush = (message) => {
  alterSyncStatus(0)
  sendMessage(message, LOG_TYPES.WARN)
}

const warnPull = (message) => {
  alert(
    'Ocorreu um erro durante a sincronização. Por favor, tente novamente mais tarde.',
  )
  sendMessage(message, LOG_TYPES.WARN)
}

const error = (message) => {
  sendMessage(message, LOG_TYPES.ERROR)
}

let currentStatus

export const alterSyncStatus = (payload) => {
  currentStatus = payload
  store.dispatch({
    type: CHANGE_CURRENT_SYNC_VIEW,
    payload: payload,
  })
}

export const sleep = async (milliseconds) => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

let isForcedSync = false
let isSyncing = false

export const forceSync = async (isOnlyPull = false, isOnlyPush = true) => {
  if (!isSyncing) {
    isForcedSync = true
    try {
      await sync(isOnlyPull, isOnlyPush)
    } catch (e) {
      warnPull(`There was an error trying to force sync: ${e}`)
    }
    isForcedSync = false
  } else {
    //TODO: Solve this loop in another way (this is so we can away for forceSync so it guarantee anyway that a sync was made and finished, so we can dispatch endLoading)
    await sleep(SLEEP_TIME / 10)
    if (isSyncing) {
      return forceSync(false)
    }
  }
}

// Debug hooks (dev-only) — mesmo padrão de window.__SYNC_QUEUE__.
// Permite smoke-testar push de eventos sem esperar o loop de 30s:
//   window.__SYNC__.forceSync()         → roda push agora
//   window.__SYNC__.forcePushEvents()   → roda só pushEvents (precisa de token)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.__SYNC__ = {
    forceSync,
    forcePushEvents: async () => {
      const auth = await getLoggedUser()
      if (!auth?.token) {
        console.warn('[__SYNC__] sem token — faça login primeiro')
        return
      }
      return pushEvents(auth.token)
    },
  }
}

export const startSyncingLoop = async () => {
  // ================================================================
  // CORREÇÃO T1.4: Verificar checkpoints não recuperados ao iniciar
  // ================================================================
  if (USE_ENHANCED_SYNC) {
    try {
      await checkpointManager.cleanupOldCheckpoints()

      // Verificar se há recuperação pendente
      const recoveryResult = await checkpointManager.attemptRecovery({
        // Handler para re-enfileirar operações de push não confirmadas
        requeue: async (payload, entryIds, entityType) => {
          await syncQueue.enqueue({
            entityType,
            payload,
            entryIds,
            createdAt: Date.now(),
            isRecovery: true,
          })
        },
        // Handler para marcar como synced após verificação
        markAsSynced: async (entityType, entryIds) => {
          // Será tratado no próximo ciclo de sync
          syncLogger.log('RECOVERY_MARK_SYNCED_DEFERRED', {
            entityType,
            count: entryIds?.length,
          })
        },
      })

      if (recoveryResult.needed) {
        syncLogger.log('STARTUP_RECOVERY_RESULT', recoveryResult)
      }
    } catch (recoveryError) {
      syncLogger.error('STARTUP_RECOVERY_ERROR', recoveryError)
    }
  }

  await sleep(SLEEP_TIME)
  avoidConcurrentSync()
}

async function avoidConcurrentSync() {
  if (!isForcedSync && !isSyncing) {
    await sync()
  }
  await sleep(SLEEP_TIME)
  avoidConcurrentSync()
}

async function sync(isOnlyPull = false, isOnlyPush = true) {
  if (!hasInternet()) {
    alterSyncStatus(0)
    if (USE_ENHANCED_SYNC) {
      syncLogger.log('SYNC_SKIP_OFFLINE', {})
    }
    return
  }

  log('Checking For New Things To Sync')

  // ================================================================
  // LOGGING ESTRUTURADO: Iniciar sessão de sync
  // ================================================================
  let currentSyncId = null
  if (USE_ENHANCED_SYNC) {
    currentSyncId = syncLogger.startSync(isForcedSync ? 'forced' : 'auto')
  }

  if (database) {
    let loggedUserInfo = await getLoggedUser()
    if (isEmpty(loggedUserInfo) || isEmpty(loggedUserInfo.userId)) {
      log('No logged user detected. Skipping...')
      if (USE_ENHANCED_SYNC) {
        syncLogger.endSync(false, { reason: 'no_user' })
      }
    } else {
      isSyncing = true
      isOnlyPush && alterSyncStatus(1)

      try {
        // ================================================================
        // CORREÇÃO V04: Usar TokenManager para evitar race conditions
        // ================================================================
        let token = loggedUserInfo.token
        if (USE_ENHANCED_SYNC && !token) {
          syncLogger.log('SYNC_TOKEN_MISSING', { attempting: 'tokenManager' })
          token = await tokenManager.getValidToken(getLoggedUser, refreshToken)
        }

        // Push Changes
        log('Pushing Changes')

        // ================================================================
        // CORREÇÃO V03: Usar pushEnhanced com SyncQueue integrado
        // ================================================================
        let mustPull = true
        if (USE_ENHANCED_SYNC) {
          const pushResult = await pushEnhanced(
            token || loggedUserInfo.token,
            sendData,
            getLoggedUser,
            isOnlyPull,
          )

          // Se teve falhas no push, ainda assim fazer pull
          // mas logar o resultado
          if (!pushResult.success) {
            syncLogger.log('PUSH_HAD_FAILURES', {
              totalFailed: pushResult.totalFailed,
              queuedForRetry: pushResult.queuedForRetry,
              errors: pushResult.errors,
            })
            void recordEvent({
              level: 'warn',
              category: 'sync',
              action: 'push_cycle_had_failures',
              payload: {
                totalFailed: pushResult.totalFailed,
                queuedForRetry: pushResult.queuedForRetry,
              },
            })
          }

          // mustPull continua true - queremos fazer pull mesmo com falhas no push
          // as operações que falharam estão na fila para retry
        } else {
          // Comportamento original
          mustPull = await push(token || loggedUserInfo.token, isOnlyPull)
        }

        // Push de eventos de observabilidade — sempre roda, independente do
        // caminho (push tradicional OU pushEnhanced). Antes ficava acoplado
        // ao final de `push()`, então `pushEnhanced` órfão de eventos. Falha
        // aqui NÃO derruba o sync de entidades de negócio.
        try {
          await pushEvents(token || loggedUserInfo.token)
        } catch (eventsErr) {
          console.error('[sync] pushEvents failed (ignored)', eventsErr)
        }

        // Workaround to get a possibly updated token
        loggedUserInfo = await getLoggedUser()

        if (mustPull && !isOnlyPush) {
          log('Pulling Changes')
          await pull(loggedUserInfo)
        }

        isSyncing = false

        if (isSyncing == false && currentStatus != 0) {
          alterSyncStatus(2)
        }

        if (USE_ENHANCED_SYNC) {
          syncLogger.endSync(true, {
            pushed: true,
            pulled: mustPull && !isOnlyPush,
          })
        }
      } catch (syncError) {
        isSyncing = false
        alterSyncStatus(0)

        if (USE_ENHANCED_SYNC) {
          syncLogger.error('SYNC_ERROR', syncError, {
            phase: 'main_sync',
          })
          syncLogger.endSync(false, { error: syncError.message })
        }

        error(`Sync failed: ${syncError.message}`)
      }
    }
  } else {
    warn('Database not initialized')
    if (USE_ENHANCED_SYNC) {
      syncLogger.endSync(false, { reason: 'no_database' })
    }
  }
}

async function oldPull(loggedUserInfo, isRetry = false, forceRessinc = false) {
  const lastPulledAt = await database.adapter.getLocal(
    `${loggedUserInfo.userId}_lastPulledAt`,
  )
  const token = loggedUserInfo.token

  let params = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }

  let url = `${SYNC_URLS.PULL}${
    lastPulledAt && !forceRessinc ? `?lastPulledAt=${lastPulledAt}` : ''
  }`

  // if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
  //   url = `${SYNC_URLS.PULL_GATEWAY_VERITY}${
  //     lastPulledAt ? `?lastPulledAt=${lastPulledAt}` : ''
  //   }`
  // }

  log(`URL: ${url}`)
  log(`Fetching changes using token ${token}`)

  // ================================================================
  // T6: Circuit Breaker - Verificar se podemos fazer requisição
  // ================================================================
  if (USE_ENHANCED_SYNC) {
    const canExec = circuitBreaker.canExecute('pullFarms')
    if (!canExec.allowed) {
      syncLogger.log('PULL_FARMS_CIRCUIT_OPEN', {
        reason: canExec.reason,
        retryAfter: canExec.retryAfter,
      })
      warnPull(
        `Circuit breaker is open for pullFarms. Retry after ${canExec.retryAfter}ms`,
      )
      return
    }
  }

  // Pull Changes
  await timeoutFetch(url, params, isForcedSync ? EXTRA_TIMEOUT : SYNC_TIMEOUT)
    .catch((err) => {
      // T6: Registrar falha no circuit breaker
      if (USE_ENHANCED_SYNC) {
        circuitBreaker.recordFailure('pullFarms', err)
      }

      if (err instanceof TimeoutError) {
        warnPull(
          'Timeout trying to fetch changes. Will try again in next cycle',
        )
      } else {
        warnPull(`There was an error trying to fetch sync object: ${err}`)
      }
    })
    .then(async (response) => {
      if (!response) {
        if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
          return pull(loggedUserInfo, true)
        }
        return null
      }
      if (response.status == 401) {
        const data = await refreshToken()
        if (!isEmpty(data) && data.refreshed && !isRetry) {
          return pull(
            {
              userId: data.userId,
              token: data.token,
              pass: data.pass,
            },
            true,
          )
        }
      } else if (response.status >= 400 && response.status !== 401) {
        warnPull(`Received status: ${response.status}`)
        if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
          return pull(loggedUserInfo, true)
        }
      } else {
        const content = JSON.parse(await response.text())
        if (content.sucesso) {
          // T6: Registrar sucesso no circuit breaker
          if (USE_ENHANCED_SYNC) {
            circuitBreaker.recordSuccess('pullFarms')
          }

          log('New content received! Updating Database')
          await pullChanges(content.response)

          //TODO: Only update lastPulletAt if pullChanges was successful
          log(`LastPulletAt: ${content.response.lastPulledAt}`)
          await database.adapter.setLocal(
            `${loggedUserInfo.userId}_lastPulledAt`,
            content.response.lastPulledAt,
          )
          localStorage.setItem('lastSyncPull', new Date())
          alert(
            'Sincronização realizada com Sucesso! Seus dados foram atualizados.',
          )
        } else {
          warnPull(`Request unsuccessful. Reason: ${content.mensagem}`)
        }
      }
    })
}

async function pull(loggedUserInfo, isRetry = false, forceRessinc = false) {
  const lastPulledAt = await database.adapter.getLocal(
    `${loggedUserInfo.userId}_lastPulledAt`,
  )
  let token = loggedUserInfo.token

  // Token nulo: aguarda e tenta refresh + fallback do localStorage.
  if (!token && loggedUserInfo.userId && !isRetry) {
    await new Promise((resolve) => setTimeout(resolve, 500))

    try {
      const { getLoggedUser } = await import('./redux/auth/actions')
      const refreshedUser = await getLoggedUser()

      if (refreshedUser.token) {
        return await pull(refreshedUser, true, forceRessinc)
      }

      try {
        const fallbackToken = localStorage.getItem('fallback_token')
        if (fallbackToken) {
          token = fallbackToken
          loggedUserInfo = { ...loggedUserInfo, token: fallbackToken }
        }
      } catch (fallbackError) {
        console.warn('[pull] fallback_token check failed', fallbackError)
      }
    } catch (error) {
      console.error('[pull] error refreshing logged user', error)
    }
  }

  if (!token) {
    console.error('[pull] CRITICAL: no token available for sync operation')
  }

  let body = JSON.stringify(
    lastPulledAt
      ? {
          lastPulledAt,
        }
      : {},
  )

  let params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body,
  }
  let url = `${SYNC_URLS.HALF_PULL}${
    lastPulledAt && !forceRessinc ? `?lastPulledAt=${lastPulledAt}` : ''
  }`

  // if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
  //   url = `${SYNC_URLS.PULL_GATEWAY_VERITY}${
  //     lastPulledAt ? `?lastPulledAt=${lastPulledAt}` : ''
  //   }`
  // }

  log(`URL: ${url}`)
  log(`Fetching changes using token ${token}`)

  // ================================================================
  // T6: Circuit Breaker - Verificar se podemos fazer requisição
  // ================================================================
  if (USE_ENHANCED_SYNC) {
    const canExec = circuitBreaker.canExecute('pull')
    if (!canExec.allowed) {
      syncLogger.log('PULL_CIRCUIT_OPEN', {
        reason: canExec.reason,
        retryAfter: canExec.retryAfter,
      })
      warnPull(`Circuit breaker is open. Retry after ${canExec.retryAfter}ms`)
      return
    }
  }

  // Pull Changes
  // ================================================================
  // INSTRUMENTAÇÃO DE DIAGNÓSTICO - Medir tempo total do fetch
  // ================================================================
  const fetchStartTime = Date.now()
  const currentTimeout = isForcedSync ? EXTRA_TIMEOUT : SYNC_TIMEOUT

  await timeoutFetch(url, params, currentTimeout)
    .catch((err) => {
      const fetchDuration = Date.now() - fetchStartTime
      recordError(err, {
        category: 'sync',
        action: 'pull_fetch_failed',
        payload: {
          fetchDurationMs: fetchDuration,
          isTimeout: err instanceof TimeoutError,
        },
      })

      if (USE_ENHANCED_SYNC) {
        circuitBreaker.recordFailure('pull', err)
      }

      if (err instanceof TimeoutError) {
        warnPull(
          'Timeout trying to fetch changes. Will try again in next cycle',
        )
      } else {
        warnPull(`There was an error trying to fetch sync object: ${err}`)
      }
    })
    .then(async (response) => {
      const fetchDuration = Date.now() - fetchStartTime
      if (!response) {
        if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
          return pull(loggedUserInfo, true)
        }
        return null
      }
      if (response.status == 401) {
        const data = await refreshToken()
        if (!isEmpty(data) && data.refreshed && !isRetry) {
          return pull(
            {
              userId: data.userId,
              token: data.token,
              pass: data.pass,
            },
            true,
          )
        }
      } else if (response.status >= 400 && response.status !== 401) {
        warnPull(`Received status: ${response.status}`)
        recordError(new Error(`pull HTTP ${response.status}`), {
          category: 'sync',
          action: 'pull_http_error',
          payload: { status: response.status },
        })
        if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
          return pull(loggedUserInfo, true)
        }
      } else {
        const responseText = await response.text()
        const responseSize = responseText.length
        const content = JSON.parse(responseText)

        recordEvent({
          level: 'info',
          category: 'sync',
          action: 'pull_completed',
          payload: {
            responseSizeKb: Math.round(responseSize / 1024),
            fetchDurationMs: fetchDuration,
          },
        })

        if (content.sucesso) {
          // T6: Registrar sucesso no circuit breaker
          if (USE_ENHANCED_SYNC) {
            circuitBreaker.recordSuccess('pull')
          }

          log('New content received! Updating Database')

          // ================================================================
          // CORREÇÃO V01 & V02: Usar pullChanges melhorado com transações
          // atômicas e só atualizar lastPulledAt após sucesso confirmado
          // ================================================================
          if (USE_ENHANCED_SYNC) {
            syncLogger.log('PULL_USING_ENHANCED', {
              userId: loggedUserInfo.userId,
            })

            // Usar pullChanges com transações atômicas
            const pullResult = await pullChangesEnhanced(content.response)

            // Só atualizar lastPulledAt se pullChanges foi 100% bem-sucedido
            if (pullResult.success) {
              log(`LastPulledAt: ${content.response.lastPulledAt}`)
              await updateLastPulledAtSafely(
                loggedUserInfo.userId,
                content.response.lastPulledAt,
                pullResult,
              )
            } else {
              // Pull teve erros - NÃO atualizar lastPulledAt
              // Próximo sync tentará novamente
              syncLogger.log('PULL_PARTIAL_FAILURE', {
                errors: pullResult.errors,
                willRetryNextCycle: true,
              })
              warn(
                `Pull completed with ${pullResult.errors.length} errors. Will retry next cycle.`,
              )
            }
          } else {
            // Comportamento original (fallback)
            await pullChanges(content.response)
            log(`LastPulledAt: ${content.response.lastPulledAt}`)
            await database.adapter.setLocal(
              `${loggedUserInfo.userId}_lastPulledAt`,
              content.response.lastPulledAt,
            )
            localStorage.setItem('lastSyncPull', new Date())
          }
        } else {
          warnPull(`Request unsuccessful. Reason: ${content.mensagem}`)
        }
      }
    })
}

const downloadFarmsModified = (fazendasBaixadas, localStorageFarms) => {
  fazendasBaixadas.map((farm) => {
    localStorageFarms.map((df) => {
      if (df.id === farm.id) {
        df.lastPulledAt = farm.lastPulledAt
      } else {
        let downloadFarmsIds
        downloadFarmsIds = []
        localStorageFarms.map((downFarm) => downloadFarmsIds.push(downFarm.id))
        if (downloadFarmsIds.indexOf(farm.id) < 0) {
          localStorageFarms.push(farm)
        }
      }
    })
  })
  return localStorageFarms
}

export async function pullFarms(
  farms,
  loggedUserInfo = {},
  isRetry = false,
  forceResync = false,
  isRemoveLastPulledAtFarm = false,
  batchesId = [],
) {
  // ================================================================
  // FIX: Bloquear syncs automáticos durante pullFarms
  // Isso evita que o mesmo dado seja enviado múltiplas vezes
  // ================================================================
  // Se já há um sync em andamento, aguardar até que termine
  // (similar ao comportamento de forceSync)
  if (isSyncing && !isRetry) {
    let waitCount = 0
    const maxWait = 60 // Máximo 60 * 500ms = 30 segundos de espera
    while (isSyncing && waitCount < maxWait) {
      await sleep(500)
      waitCount++
    }
    if (isSyncing) {
      recordEvent({
        level: 'warn',
        category: 'sync',
        action: 'pull_farms_wait_timeout',
        payload: { waitedMs: waitCount * 500 },
      })
    }
  }

  const wasAlreadySyncing = isSyncing
  if (!wasAlreadySyncing) {
    isSyncing = true
  }

  loggedUserInfo = await getLoggedUser()

  const lastPulledAt = await database.adapter.getLocal(
    `${loggedUserInfo.userId}_lastPulledAt`,
  )
  const token = loggedUserInfo.token

  if (!token) {
    recordEvent({
      level: 'error',
      category: 'sync',
      action: 'pull_farms_no_token',
      payload: { userId: loggedUserInfo.userId },
    })
  }

  const lastPulledAtFarms = await Promise.all(
    farms.map(async (f) => ({
      id: f,
      lastPulledAt: !isRemoveLastPulledAtFarm
        ? await database.adapter.getLocal(`${f}_lastPulledAt`)
        : null,
    })),
  )

  const body = JSON.stringify({
    lastPulledAt,
    fazendasBaixadas: lastPulledAtFarms,
    ...(batchesId.length > 0 && { lotesBaixados: batchesId }),
  })

  const url = `${SYNC_URLS.HALF_PULL}${
    lastPulledAt && !forceResync ? `?lastPulledAt=${lastPulledAt}` : ''
  }`

  const dynamicTimeout = await calculateDynamicTimeout(farms)
  const effectiveTimeout = forceResync
    ? Math.max(EXTRA_TIMEOUT, dynamicTimeout)
    : dynamicTimeout

  recordEvent({
    level: 'info',
    category: 'sync',
    action: 'pull_farms_started',
    payload: {
      farmsCount: farms.length,
      isRetry,
      forceResync,
      hadLastPulledAt: !!lastPulledAt,
      timeoutMin: Math.round(effectiveTimeout / 60000),
    },
  })

  const params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body,
  }

  const fetchStart = Date.now()

  // P01 FIX: Atualizar UI para mostrar que está baixando dados da fazenda
  updateSyncUI(SYNC_STATUS.DOWNLOADING_FARM, {
    startTime: Date.now(),
    entity: 'farms',
  })

  try {
    const response = await timeoutFetch(url, params, effectiveTimeout)

    const fetchDuration = Date.now() - fetchStart

    if (!response) {
      recordError(new Error('pull_farms no response'), {
        category: 'sync',
        action: 'pull_farms_no_response',
        payload: { fetchDurationMs: fetchDuration },
      })
      throw new Error('Failed to fetch response')
    }

    if (response.status === 401) {
      const data = await refreshToken()
      if (!isEmpty(data) && data.refreshed && !isRetry) {
        recordEvent({
          level: 'info',
          category: 'auth',
          action: 'token_refreshed_during_pull',
        })
        if (!wasAlreadySyncing) {
          isSyncing = false
        }
        return pullFarms(
          farms,
          { userId: data.userId, token: data.token },
          true,
          forceResync,
          isRemoveLastPulledAtFarm,
          batchesId,
        )
      }
      recordError(new Error('pull_farms token refresh failed'), {
        category: 'auth',
        action: 'token_refresh_failed',
      })
    } else if (response.status >= 400) {
      recordError(new Error(`pull_farms HTTP ${response.status}`), {
        category: 'sync',
        action: 'pull_farms_http_error',
        payload: { status: response.status },
      })
      throw new Error(`Received status: ${response.status}`)
    }

    const responseText = await response.text()
    const responseSize = responseText.length
    const content = JSON.parse(responseText)

    updateSyncUI(SYNC_STATUS.PROCESSING)

    if (content.sucesso) {
      const newLastPulledAt = content.response.lastPulledAt

      if (newLastPulledAt && newLastPulledAt !== lastPulledAt) {
        // ================================================================
        // CORREÇÃO V01 & V02: Usar pullChanges melhorado em pullFarms
        // ================================================================
        if (USE_ENHANCED_SYNC) {
          syncLogger.log('PULL_FARMS_USING_ENHANCED', {
            farmCount: farms.length,
            batchesCount: batchesId.length,
            forceResync,
          })

          // FIX: Passar forceResync como forceUpdate para ignorar comparação de timestamps
          // Quando o usuário clica SINCRONIZAR (forceResync=true), sempre atualizar dados do servidor
          const pullResult = await pullChangesEnhanced(
            content.response,
            batchesId.length > 0,
            null, // onProgress
            forceResync, // forceUpdate - quando true, usa >= em vez de > na comparação de timestamps
          )

          if (pullResult.success) {
            await updateLastPulledAtSafely(
              loggedUserInfo.userId,
              newLastPulledAt,
              pullResult,
            )
            await updateFarmLastPulledAtSafely(
              farms,
              newLastPulledAt,
              pullResult,
            )
          } else {
            syncLogger.log('PULL_FARMS_PARTIAL_FAILURE', {
              errors: pullResult.errors,
              farms,
            })
            recordEvent({
              level: 'warn',
              category: 'sync',
              action: 'pull_farms_partial_failure',
              payload: {
                errorsCount: pullResult.errors.length,
                farmsCount: farms.length,
                failedEntities: pullResult.errors
                  .slice(0, 10)
                  .map((e) => e.entity || e.error || String(e)),
              },
            })
          }
        } else {
          await pullChanges(content.response, batchesId.length > 0, forceResync)
          await database.adapter.setLocal(
            `${loggedUserInfo.userId}_lastPulledAt`,
            newLastPulledAt,
          )
          await Promise.all(
            farms.map((f) =>
              database.adapter.setLocal(`${f}_lastPulledAt`, newLastPulledAt),
            ),
          )
        }
      }

      const storedFarms =
        JSON.parse(localStorage.getItem('downloadFarms')) || []
      const farmsMap = new Map(storedFarms.map((f) => [f.id, f]))
      lastPulledAtFarms.forEach((farm) => farmsMap.set(farm.id, farm))
      const updatedFarms = Array.from(farmsMap.values())
      localStorage.setItem('downloadFarms', JSON.stringify(updatedFarms))

      const syncTimestamp = new Date().toISOString()
      localStorage.setItem('lastSyncPull', syncTimestamp)

      updateSyncUI(SYNC_STATUS.SUCCESS)

      recordEvent({
        level: 'info',
        category: 'sync',
        action: 'pull_farms_completed',
        payload: {
          farmsCount: farms.length,
          fetchDurationMs: fetchDuration,
          responseSizeKb: Math.round(responseSize / 1024),
          hadNewData: !!(newLastPulledAt && newLastPulledAt !== lastPulledAt),
        },
      })
    } else {
      updateSyncUI(SYNC_STATUS.ERROR)
      const errorMsg = `Request unsuccessful. Reason: ${content.mensagem}`
      warnPull(errorMsg)
      recordError(new Error(errorMsg), {
        category: 'sync',
        action: 'pull_farms_server_failure',
        payload: { mensagem: content.mensagem },
      })
      if (!wasAlreadySyncing) {
        isSyncing = false
      }
      throw new Error(errorMsg)
    }
  } catch (error) {
    recordError(error, {
      category: 'sync',
      action: 'pull_farms_failed',
      payload: { isRetry, farmsCount: farms.length },
    })
    console.error('[pullFarms] sync error', error)

    warnPull(`Error during sync: ${error.message}`)

    if (!isRetry) {
      if (!wasAlreadySyncing) {
        isSyncing = false
      }
      return pullFarms(
        farms,
        loggedUserInfo,
        true,
        forceResync,
        isRemoveLastPulledAtFarm,
        batchesId,
      )
    } else {
      updateSyncUI(SYNC_STATUS.ERROR)
      if (!wasAlreadySyncing) {
        isSyncing = false
      }
      throw error
    }
  }

  if (!wasAlreadySyncing) {
    isSyncing = false
  }
}

/**
 * pullFarmsWithRetry - Wrapper com retry e backoff exponencial
 *
 * Tenta sincronizar fazendas com múltiplas tentativas, aumentando
 * o timeout a cada falha para lidar com conexões lentas.
 *
 * @param {string[]} farms - IDs das fazendas
 * @param {Object} loggedUserInfo - Info do usuário logado
 * @param {Object} options - Opções de configuração
 * @param {number} options.maxRetries - Número máximo de tentativas (default: 3)
 * @param {number} options.baseDelayMs - Delay base entre tentativas (default: 5000)
 * @param {Function} options.onRetry - Callback chamado em cada retry
 * @param {Function} options.onProgress - Callback de progresso
 * @returns {Promise<boolean>} true se sucesso, false se todas tentativas falharam
 */
export async function pullFarmsWithRetry(
  farms,
  loggedUserInfo = {},
  options = {},
) {
  const {
    maxRetries = 3,
    baseDelayMs = 5000,
    onRetry = () => {},
    onProgress = () => {},
    forceResync = false,
    isRemoveLastPulledAtFarm = false,
    batchesId = [],
  } = options

  // P07 FIX: Helper para chamar callbacks de forma segura
  const safeCallback = (callback, data) => {
    try {
      callback(data)
    } catch (callbackError) {
      console.warn(
        '[PULL-FARMS-RETRY] Callback error (ignorado):',
        callbackError.message,
      )
    }
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      updateSyncUI(SYNC_STATUS.DOWNLOADING_FARM, {
        attempt,
        maxAttempts: maxRetries,
      })

      safeCallback(onProgress, { attempt, maxRetries, status: 'trying' })

      await pullFarms(
        farms,
        loggedUserInfo,
        true,
        forceResync,
        isRemoveLastPulledAtFarm,
        batchesId,
      )

      safeCallback(onProgress, { attempt, maxRetries, status: 'success' })
      return true
    } catch (error) {
      const isLastAttempt = attempt === maxRetries
      const isTimeoutError =
        error instanceof TimeoutError ||
        error.message?.includes('TIMEOUT') ||
        error.message?.includes('timeout')

      if (isLastAttempt) {
        recordError(error, {
          category: 'sync',
          action: 'pull_farms_retry_exhausted',
          payload: { attempts: maxRetries, isTimeout: isTimeoutError },
        })

        updateSyncUI(SYNC_STATUS.ERROR)

        safeCallback(onProgress, {
          attempt,
          maxRetries,
          status: 'failed',
          error,
        })
        return false
      }

      const retryDelay = baseDelayMs * Math.pow(1.5, attempt - 1)

      // P01 FIX: Atualizar UI para mostrar que está tentando novamente
      updateSyncUI(SYNC_STATUS.RETRYING, {
        attempt,
        maxAttempts: maxRetries,
      })

      // P07 FIX: Notificar sobre retry usando safeCallback
      safeCallback(onRetry, {
        attempt,
        maxRetries,
        error,
        isTimeoutError,
        nextRetryDelayMs: retryDelay,
      })

      safeCallback(onProgress, {
        attempt,
        maxRetries,
        status: 'retrying',
        nextRetryDelayMs: retryDelay,
      })

      // Aguardar antes de tentar novamente
      await sleep(retryDelay)
    }
  }

  return false
}

async function createPayloadFor(entry, value, firstPass = true, verb = 'POST') {
  let mapping =
    AVAILABLE_DTO_MAPPINGS[
      firstPass ? toDTOMapping(SYNC_OBJECT_TO_TABLE[value]) : value
    ]
  let json = toDTO(mapping, entry)
  if (mapping.children) {
    for (const child of mapping.children) {
      //console.warn(entry)
      json[child.name] = await Promise.all(
        (await entry[child.name].fetch())
          .filter((element) => !element.isDeleted)
          .map(
            async (element) =>
              await createPayloadFor(element, child.type, false),
          ),
      )
    }
  }

  if (mapping.renamed) {
    for (const field of mapping.renamed) {
      if (field.isDate) {
        json[field.dtoName] = utils.fromMoment(json[field.dtoName])
      }
    }
  }

  if (firstPass) {
    let payload = {
      verbo: verb,
      json: {
        ...json,
        createdAt: json.createdAt.valueOf(),
        updatedAt: json.updatedAt.valueOf(),
      },
    }

    return payload
  } else {
    return {
      ...json,
      createdAt: json.createdAt.valueOf(),
      updatedAt: json.updatedAt.valueOf(),
    }
  }
}

function normalizeEntityName(name) {
  if (name === 'fazendaUsuarios') return 'fazenda_usuario'
  return name.substring(0, name.length - 1) //remove 's'
}

/**
 * push — IMPLEMENTAÇÃO LEGACY. Não é usada em produção desde
 * `USE_ENHANCED_SYNC = true` (default hardcoded em syncWorker.js:50).
 *
 * ⚠️ AO ADICIONAR FEATURES A SYNC: replique em `pushEnhanced` (em
 * `src/sync/syncWorkerEnhanced.js`) OU coloque a chamada em `sync()` após
 * a bifurcação (linha ~383). Caso contrário a feature fica órfã em prod.
 *
 * Histórico: `pushEvents` foi adicionado aqui no commit 70a6915d (mai/2026)
 * sem equivalente em pushEnhanced; eventos só pegaram carona pelo immediate-
 * push de erros até ser detectado e movido pra sync() em mai/2026.
 *
 * Plano: remover em `docs/superpowers/plans/2026-05-12-remove-legacy-push.md`.
 */
async function push(token, isOnlyPull = false) {
  // Token nulo: tenta refresh + fallback do localStorage.
  if (!token) {
    try {
      const { getLoggedUser } = await import('./redux/auth/actions')
      const loggedUserInfo = await getLoggedUser()

      if (loggedUserInfo.token) {
        token = loggedUserInfo.token
      } else {
        try {
          const fallbackToken = localStorage.getItem('fallback_token')
          if (fallbackToken) {
            token = fallbackToken
          }
        } catch (fallbackError) {
          console.warn('[push] fallback_token check failed', fallbackError)
        }
      }
    } catch (error) {
      console.error('[push] error refreshing logged user', error)
    }
  }

  if (!token) {
    console.error('[push] CRITICAL: no token available for push operation')
    return
  }

  let loggedUser = await (await getLoggedUser()).userId
  var mustPull = true
  for (let value of [
    'fazendas',
    'retiros',
    'protocolos',
    'lotes',
    'd0s',
    'dns',
    'iatfs',
    'fazendaUsuarios',
    'touros',
    'partidas',
  ]) {
    await database.action(async () => {
      const collection = database.collections.get(SYNC_OBJECT_TO_TABLE[value])
      const entityEntry = await database.collections
        .get('domain_values')
        .query(
          Q.on('domain_types', 'nome', 'entidade'),
          Q.where('valor', normalizeEntityName(value)),
        )
        .fetch()
      const notSyncedQuery = Q.where('_status', Q.notEq('synced'))
      const toSync = await collection.query(notSyncedQuery).fetch()

      const updatedIds = compact(
        toSync.map((x) => x.syncStatus === 'updated' && x.id),
      )

      let toMarkAsSynced = []

      var contentPayload = new Array()
      var entries = new Array()

      // G007: chunk() sem size produzia chunks de 1 — o processamento já era
      // serial. Iteração direta explicita isso sem mudar comportamento.
      for (const entry of toSync) {
        const verb =
          updatedIds.includes(entry.id) && !entry.priority
            ? 'PUT'
            : updatedIds.includes(entry.id) && entry.priority
              ? 'PRIORITY'
              : 'POST'

        contentPayload.push(await createPayloadFor(entry, value, true, verb))

        entries.push(entry)
      }
      if (contentPayload.length > 0) {
        let payload = {
          tipoId: entityEntry[0].id,
          loggedUser,
          contents: contentPayload,
        }

        const dataSent = isOnlyPull ? true : await sendData(payload, token)

        if (dataSent) {
          log('Data sent. Marking as synced!')
          for (const item of entries) {
            toMarkAsSynced.push(item)
          }
        } else {
          mustPull = false
        }
      }

      // Remove possibly updated entries after process started (they will be synced in the next cycle)
      toMarkAsSynced = compact(
        await Promise.all(
          toMarkAsSynced.map(async (entry) => {
            const localEntry = await collection.find(entry.id)
            const entryUpdatedAt = utils.fromMoment(
              utils.toMoment(entry._raw.updated_at),
            )
            const localEntryUpdatedAt = utils.fromMoment(
              utils.toMoment(localEntry._raw.updated_at),
            )
            if (
              localEntry &&
              isNumber(entryUpdatedAt) &&
              isNumber(localEntryUpdatedAt) &&
              entryUpdatedAt - localEntryUpdatedAt >= 0
            ) {
              return entry
            } else {
              return null
            }
          }),
        ),
      )

      await database.batch(
        ...toMarkAsSynced.map((entry) => prepareMarkAsSynced(entry)),
      )
    })
  }

  // pushEvents agora é chamado em sync() após os branches push/pushEnhanced,
  // pra cobrir os dois caminhos consistentemente. Ver syncWorker.js:~383.

  return mustPull
}

export async function downloadProofOfManagement({ values, text, batchIds }) {
  const loggedUser = (await getLoggedUser()).userId
  let comprovante = ''

  void recordEvent({
    level: 'info',
    category: 'outro',
    action: 'proof_of_management_started',
    payload: {
      batchCount: Array.isArray(batchIds) ? batchIds.length : 0,
      entityCount: Array.isArray(values) ? values.length : 0,
    },
  })

  const allValues = ['fazendas', 'retiros', 'protocolos'].concat(values)

  // Função para lidar com a ação do banco de dados
  async function handleDatabaseAction(value) {
    const collection = database.collections.get(SYNC_OBJECT_TO_TABLE[value])
    const entityEntry = await database.collections
      .get('domain_values')
      .query(
        Q.on('domain_types', 'nome', 'entidade'),
        Q.where('valor', normalizeEntityName(value)),
      )
      .fetch()

    const valuesInBatch = Q.or(
      Q.where('batch_id', Q.oneOf(batchIds)),
      Q.where('id', Q.oneOf(batchIds)),
    )
    const notSyncedQuery = Q.where('_status', Q.notEq('synced'))
    const combinedQuery = Q.or(valuesInBatch, notSyncedQuery)

    const toSync = await collection.query(combinedQuery).fetch()
    const updatedIds = compact(
      toSync.map((x) => x.syncStatus === 'updated' && x.id),
    )
    let contentPayload = []

    // G007: chunk() sem size produzia chunks de 1 — o processamento já era
    // serial. Iteração direta explicita isso sem mudar comportamento.
    for (const entry of toSync) {
      const verb =
        updatedIds.includes(entry.id) && !entry.priority
          ? 'PUT'
          : updatedIds.includes(entry.id) && entry.priority
            ? 'PRIORITY'
            : 'POST'
      const payload = await createPayloadFor(entry, value, true, verb)
      contentPayload.push(payload)
    }

    if (contentPayload.length > 0) {
      let payload = {
        tipoId: entityEntry[0].id,
        loggedUser,
        contents: contentPayload,
      }
      const str = JSON.stringify(payload)
      comprovante = comprovante !== '' ? `[${comprovante},${str}]` : str
    }

    return comprovante
  }

  // Loop through values and call the function
  for (let value of allValues) {
    comprovante = await handleDatabaseAction(value)
  }

  const element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(comprovante),
  )
  element.setAttribute('download', text)
  element.style.display = 'none'
  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)

  void recordEvent({
    level: 'info',
    category: 'outro',
    action: 'proof_of_management_generated',
    payload: {
      batchCount: Array.isArray(batchIds) ? batchIds.length : 0,
      filename: text,
      payloadBytes: comprovante.length,
      isEmpty: comprovante.length === 0,
    },
  })
}

// Trava de módulo: impede envios concorrentes (sync() × triggerImmediatePush).
let pushingEvents = false

/**
 * pushEvents — envia eventos da tabela local `events` para a API.
 *
 * Append-only client→server (não recebe nada de volta pelo /half-sync).
 * Sempre ligado — historicamente gated por REACT_APP_TOGGLE_EVENTS_API, removida
 * em 2026-05-12 porque o env var causava bugs silenciosos de propagação em CI/CD.
 * Falha aqui NÃO afeta o push principal de entidades de negócio — caller deve
 * envolver em try/catch e ignorar erros para preservar a estabilidade do sync.
 *
 * Estratégia:
 *  - Lê events com `_status === 'created'` (não enviados ainda).
 *  - Envia em lotes de até EVENTS_PUSH_BATCH_SIZE.
 *  - Em sucesso (2xx) DELETA os ids retornados do DB local (`prepareDestroyPermanently`).
 *    Append-only: servidor é source-of-truth; local é só buffer de trânsito.
 *    Dashboard de Observabilidade lê via API, não do local.
 *  - 401: tenta refreshToken e refaz uma vez (mesma política de sendData).
 *  - Lê do mais antigo para o mais novo (`timestamp` ASC).
 *  - Se a resposta trouxer `rejected: [{id, reason}]`, esses ids (rejeição
 *    permanente) também são destruídos localmente e um único evento
 *    `events_rejected` agregado é registrado. Sem `rejected`, comportamento legado.
 *  - Lote sem confirmação (`received` vazio, sem `rejected`) permanece na fila e
 *    o loop segue para o próximo lote (sem head-of-line).
 *  - 4xx/5xx/rede: o lote NÃO é descartado; aborta o ciclo e tenta no próximo.
 *  - Trava de módulo: sync() e triggerImmediatePush não enviam em paralelo.
 */
export async function pushEvents(token) {
  if (!token) return
  if (pushingEvents) return
  pushingEvents = true
  try {
    await _pushEventsLocked(token)
  } finally {
    pushingEvents = false
  }
}

async function _pushEventsLocked(token) {
  const collection = database.collections.get('events')

  let pending
  try {
    pending = await collection
      .query(
        Q.where('_status', 'created'),
        Q.experimentalSortBy('timestamp', Q.asc),
      )
      .fetch()
  } catch (err) {
    console.error('[pushEvents] failed to query pending events', err)
    return
  }

  if (!pending || pending.length === 0) return

  const loggedUserInfo = await getLoggedUser()
  const userId = loggedUserInfo?.userId ?? null

  // Rejeições permanentes já descartadas localmente (agregadas em 1 evento).
  const rejectedAll = []
  const rejectedActionById = new Map()

  // Processa em lotes para limitar o tamanho do request.
  for (let i = 0; i < pending.length; i += EVENTS_PUSH_BATCH_SIZE) {
    const slice = pending.slice(i, i + EVENTS_PUSH_BATCH_SIZE)

    const payload = {
      userId,
      events: slice.map((e) => ({
        id: e.id,
        timestamp: e.timestamp,
        level: e.level,
        category: e.category,
        action: e.action,
        payload: e.payload,
        userId: e.userId ?? null,
        farmId: e.farmId ?? null,
        deviceId: e.deviceId ?? null,
        createdAt: e._raw.created_at,
      })),
    }

    const { ok, body } = await _sendEvents(payload, token)
    if (!ok) {
      // Falha transitória (rede/401/não-2xx) — mantém tudo, tenta no próximo ciclo
      break
    }

    const { received, rejected } = classifyEventsResponse(
      body,
      slice.map((e) => e.id),
    )
    // `unresolved` (nem received nem rejected) permanece na fila.

    const settledIds = new Set([...received, ...rejected.map((r) => r.id)])
    const toDelete = slice.filter((e) => settledIds.has(e.id))
    if (toDelete.length === 0) continue

    try {
      await database.action(async () => {
        await database.batch(
          ...toDelete.map((entry) => entry.prepareDestroyPermanently()),
        )
      })
    } catch (deleteErr) {
      console.error('[pushEvents] failed to delete synced events', deleteErr)
      continue
    }

    const actionById = new Map(slice.map((e) => [e.id, e.action]))
    for (const r of rejected) {
      rejectedAll.push(r)
      rejectedActionById.set(r.id, actionById.get(r.id))
    }
  }

  // Um único evento agregado. Anti-loop: não emite se TODOS os rejeitados
  // forem eles próprios `events_rejected`. level 'warn' não dispara push imediato.
  const hasNonSelf = rejectedAll.some(
    (r) => rejectedActionById.get(r.id) !== 'events_rejected',
  )
  if (rejectedAll.length > 0 && hasNonSelf) {
    await recordEvent({
      level: 'warn',
      category: 'outro',
      action: 'events_rejected',
      payload: summarizeRejections(rejectedAll),
    })
  }
}

/**
 * Envia um payload de events. Retorna `{ ok, body }`: `ok=false` em rede, 401 sem
 * refresh ou não-2xx; em 2xx, `body` é o JSON da resposta (ou null se o parse
 * falhar). Faz 1 retry em 401 (refreshToken).
 */
async function _sendEvents(payload, token, isRetry = false) {
  const params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }

  let response
  try {
    response = await timeoutFetch(SYNC_URLS.EVENTS, params)
  } catch (err) {
    console.warn('[pushEvents] network error', err)
    return { ok: false, body: null }
  }

  if (!response) return { ok: false, body: null }

  if (response.status === 401 && !isRetry) {
    const dataToken = await refreshToken()
    if (!isEmpty(dataToken) && dataToken.refreshed) {
      return _sendEvents(payload, dataToken.token || token, true)
    }
    return { ok: false, body: null }
  }

  if (response.status >= 200 && response.status < 300) {
    try {
      return { ok: true, body: await response.json() }
    } catch {
      return { ok: true, body: null }
    }
  }

  console.warn(`[pushEvents] non-2xx status: ${response.status}`)
  return { ok: false, body: null }
}

export async function sendData(data, token, isRetry = false) {
  // ================================================================
  // T6: Circuit Breaker - Verificar se podemos fazer requisição
  // ================================================================
  if (USE_ENHANCED_SYNC) {
    const canExec = circuitBreaker.canExecute('push')
    if (!canExec.allowed) {
      syncLogger.log('PUSH_CIRCUIT_OPEN', {
        reason: canExec.reason,
        retryAfter: canExec.retryAfter,
      })
      warnPush(`Circuit breaker is open. Retry after ${canExec.retryAfter}ms`)
      return false
    }
  }

  let params = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }
  let url = `${SYNC_URLS.PUSH}`

  // if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
  //   url = `${SYNC_URLS.PUSH_GATEWAY_VERITY}`
  // }

  return timeoutFetch(url, params)
    .catch((err) => {
      // T6: Registrar falha no circuit breaker
      if (USE_ENHANCED_SYNC) {
        circuitBreaker.recordFailure('push', err)
      }

      let message = ''
      if (err instanceof TimeoutError) {
        message = 'Timeout trying to send changes. Will try again in next cycle'
        warnPush(message)
      } else {
        message = `There was an error trying to send sync object: ${err}`
        warnPush(message)
      }
      return false
    })
    .then(async (response) => {
      if (!response) {
        if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
          return sendData(data, token, true)
        }
        warnPush(`There was an error trying to send sync object`)
        return null
      }
      if (response.status === 401) {
        warnPush(`There was an error trying to send sync object`)
        const dataToken = await refreshToken()

        if (!isEmpty(dataToken) && dataToken.refreshed && !isRetry) {
          return sendData(data, token, true)
        }
      }
      if (response.status >= 400 && response.status !== 401) {
        // T6: Registrar falha no circuit breaker para erros de servidor
        if (USE_ENHANCED_SYNC) {
          circuitBreaker.recordFailure(
            'push',
            new Error(`HTTP ${response.status}`),
          )
        }
        warnPush(`Received status: ${response.status}`)
        if (REACT_APP_TOGGLE_GATEWAY_VERITY && !isRetry) {
          return sendData(data, token, true)
        }
      } else if (response.status >= 200 && response.status <= 299) {
        // T6: Registrar sucesso no circuit breaker
        if (USE_ENHANCED_SYNC) {
          circuitBreaker.recordSuccess('push')
        }
        return true
      }
      return false
    })
}

//TODO: Check if we can manually modify updatedAt
//TODO: Solve Delete
async function pullChanges(
  syncObject,
  isOnlyBatch = false,
  forceUpdate = false,
) {
  let values = []

  if (isOnlyBatch) {
    values = ['d0s', 'dns', 'iatfs', 'lotes']
  } else {
    values = [
      'usuarios',
      'tipoDominios',
      'valorDominios',
      'fazendas',
      'retiros',
      'protocolos',
      'lotes',
      'd0s',
      'dns',
      'iatfs',
      'fazendaUsuarios',
      'touros',
      'partidas',
    ]
  }
  for (const value of values) {
    // OTIMIZAÇÃO G01: Pular se não houver dados para esta entidade
    if (
      !syncObject[value] ||
      !Array.isArray(syncObject[value]) ||
      syncObject[value].length === 0
    ) {
      continue
    }

    await database.action(async () => {
      const entityStartTime = Date.now()
      const collection = database.collections.get(
        isOnlyBatch
          ? SYNC_OBJECT_TO_TABLE_ONLY_BATCH[value]
          : SYNC_OBJECT_TO_TABLE[value],
      )

      // OTIMIZAÇÃO G01: Criar Map para acesso O(1) em vez de find() O(n)
      // Isso reduz complexidade de O(n²) para O(n)
      // BUG FIX: Filtrar items sem ID válido para evitar perda silenciosa de dados
      const validItems = syncObject[value].filter((item) => item.id != null)
      const serverDataMap = new Map(validItems.map((item) => [item.id, item]))

      // Items filtrados (sem id) — registra como evento warn pra atendimento.
      if (validItems.length < syncObject[value].length) {
        recordEvent({
          level: 'warn',
          category: 'sync',
          action: 'items_without_id_ignored',
          payload: {
            entity: value,
            ignoredCount: syncObject[value].length - validItems.length,
          },
        })
      }

      const existingEntries = await collection
        .query(
          Q.where(
            'id',
            Q.oneOf(syncObject[value].map((element) => element.id)),
          ),
        )
        .fetch()

      // OTIMIZAÇÃO G01: Criar Set de IDs existentes para lookup O(1)
      const existingIdsSet = new Set(existingEntries.map((e) => e.id))

      // OTIMIZAÇÃO G01: Filtrar usando Set.has() em vez de find()
      const toInsert = syncObject[value].filter(
        (element) => !existingIdsSet.has(element.id),
      )

      // OTIMIZAÇÃO G02: Pré-calcular timestamps do servidor uma vez
      // e armazenar no Map para evitar conversões repetidas
      // BUG FIX: new Date() não lança exceção para valores inválidos,
      // apenas retorna NaN. Verificar explicitamente.
      const serverTimestamps = new Map()
      for (const [id, item] of serverDataMap) {
        const timestamp = new Date(item.updatedAt).getTime()
        serverTimestamps.set(id, isNaN(timestamp) ? 0 : timestamp)
      }

      // OTIMIZAÇÃO G01+G02: Filtrar entradas para update usando Map O(1)
      // FIX: Quando forceUpdate=true, usar >= em vez de > para garantir atualização
      const entriesToUpdate = existingEntries.filter((entry) => {
        const serverItem = serverDataMap.get(entry.id)
        if (!serverItem) return false

        const serverTimestamp = serverTimestamps.get(entry.id)
        const localTimestamp = new Date(entry._raw.updated_at).getTime()

        // Quando forceUpdate=true (SINCRONIZAR clicado), sempre atualizar se timestamps iguais
        return forceUpdate
          ? serverTimestamp >= localTimestamp
          : serverTimestamp > localTimestamp
      })

      // Preparar operações de batch
      const batchOperations = []

      // Updates - usando Map para acesso O(1)
      // FIX: Forçar _status='synced' após prepareUpdate
      // O WatermelonDB automaticamente muda _status para 'updated' em prepareUpdate(),
      // o que causa reenvio no próximo push. Precisamos forçar status como synced.
      for (const entry of entriesToUpdate) {
        const serverItem = serverDataMap.get(entry.id)
        batchOperations.push(
          entry.prepareUpdate((record) => {
            fromDTO(
              AVAILABLE_DTO_MAPPINGS[toDTOMapping(SYNC_OBJECT_TO_TABLE[value])],
              serverItem,
              record,
              true,
            )
            // CRÍTICO: Forçar status como synced para evitar reenvio
            record._raw._status = 'synced'
            record._raw._changed = ''
          }),
        )
      }

      // Inserts
      for (const serverItem of toInsert) {
        batchOperations.push(
          collection.prepareCreate((newEntry) => {
            newEntry._raw.id = serverItem.id
            newEntry._raw._status = 'synced'
            newEntry._raw._changed = ''
            fromDTO(
              AVAILABLE_DTO_MAPPINGS[toDTOMapping(SYNC_OBJECT_TO_TABLE[value])],
              serverItem,
              newEntry,
              true,
            )
          }),
        )
      }

      // Executar batch principal
      if (batchOperations.length > 0) {
        await database.batch(...batchOperations)
      }

      // OTIMIZAÇÃO G03: Não chamar prepareMarkAsSynced para entradas já synced
      // Esse código foi REMOVIDO pois era redundante - registros synced não precisam ser remarcados

      // Pull lento por entidade vira evento (atendimento pode ver gargalos em campo).
      const entityDuration = Date.now() - entityStartTime
      if (entityDuration > 1000) {
        recordEvent({
          level: 'warn',
          category: 'sync',
          action: 'slow_entity_pull',
          payload: {
            entity: value,
            durationMs: entityDuration,
            recordsCount: syncObject[value].length,
            updatesCount: entriesToUpdate.length,
            insertsCount: toInsert.length,
          },
        })
      }

      if (value === 'protocolos') {
        const managementProtocolsObject = flattenDeep(
          syncObject[value].map((protocol) => {
            each(protocol.managementProtocols, function (element) {
              element.managementProtocolId =
                element.managementProtocolId || protocol.id
            })
            return protocol.managementProtocols
          }),
        )

        if (managementProtocolsObject.length === 0) {
          // Skip if no management protocols
        } else {
          // Solve ManagementProtocols
          const managementCollection = database.collections.get(
            'management_protocols',
          )

          // OTIMIZAÇÃO G01: Criar Map para acesso O(1)
          // BUG FIX: Filtrar items sem ID válido
          const validMpItems = managementProtocolsObject.filter(
            (item) => item.id != null,
          )
          const mpDataMap = new Map(validMpItems.map((item) => [item.id, item]))

          if (validMpItems.length < managementProtocolsObject.length) {
            recordEvent({
              level: 'warn',
              category: 'sync',
              action: 'items_without_id_ignored',
              payload: {
                entity: 'managementProtocols',
                ignoredCount:
                  managementProtocolsObject.length - validMpItems.length,
              },
            })
          }

          const existingManagementEntries = await managementCollection
            .query(
              Q.where(
                'id',
                Q.oneOf(managementProtocolsObject.map((entry) => entry.id)),
              ),
            )
            .fetch()

          // OTIMIZAÇÃO G01: Criar Set para lookup O(1)
          const existingMpIdsSet = new Set(
            existingManagementEntries.map((e) => e.id),
          )

          // OTIMIZAÇÃO G01: Filtrar usando Set.has() em vez de find()
          const toInsertManagementEntries = managementProtocolsObject.filter(
            (element) => !existingMpIdsSet.has(element.id),
          )

          // OTIMIZAÇÃO G02: Pré-calcular timestamps
          // BUG FIX: Verificar NaN explicitamente
          const mpTimestamps = new Map()
          for (const [id, item] of mpDataMap) {
            const timestamp = new Date(item.updatedAt).getTime()
            mpTimestamps.set(id, isNaN(timestamp) ? 0 : timestamp)
          }

          // OTIMIZAÇÃO G01+G02: Filtrar usando Map O(1)
          // FIX: Quando forceUpdate=true, usar >= em vez de >
          const mpToUpdate = existingManagementEntries.filter((entry) => {
            const serverItem = mpDataMap.get(entry.id)
            if (!serverItem) return false

            const serverTimestamp = mpTimestamps.get(entry.id)
            const localTimestamp = new Date(entry._raw.updated_at).getTime()

            return forceUpdate
              ? serverTimestamp >= localTimestamp
              : serverTimestamp > localTimestamp
          })

          // Preparar operações de batch
          const mpBatchOps = []

          // Updates usando Map O(1)
          // FIX: Forçar _status='synced' após prepareUpdate para evitar reenvio
          for (const entry of mpToUpdate) {
            const serverItem = mpDataMap.get(entry.id)
            mpBatchOps.push(
              entry.prepareUpdate((record) => {
                fromDTO(
                  AVAILABLE_DTO_MAPPINGS.MANAGEMENT_PROTOCOLS,
                  serverItem,
                  record,
                  true,
                )
                // CRÍTICO: Forçar status como synced
                record._raw._status = 'synced'
                record._raw._changed = ''
              }),
            )
          }

          // Inserts
          for (const serverItem of toInsertManagementEntries) {
            mpBatchOps.push(
              managementCollection.prepareCreate((newEntry) => {
                newEntry._raw.id = serverItem.id
                newEntry._raw._status = 'synced'
                fromDTO(
                  AVAILABLE_DTO_MAPPINGS.MANAGEMENT_PROTOCOLS,
                  serverItem,
                  newEntry,
                  true,
                )
              }),
            )
          }

          if (mpBatchOps.length > 0) {
            await database.batch(...mpBatchOps)
          }

          // OTIMIZAÇÃO G03: REMOVIDO prepareMarkAsSynced para entradas já synced
        }
      }

      if (value === 'usuarios') {
        const rolesCollection = database.collections.get('roles')
        const rolesObject = flattenDeep(
          syncObject[value].map((user) =>
            (user.roles || []).map((element) => ({
              backend_id: element.id,
              name: element.name,
              description: element.description,
              userId: user.id,
            })),
          ),
        )

        if (rolesObject.length === 0) {
          // Skip if no roles
        } else {
          const existingRolesEntries = await rolesCollection
            .query(
              Q.where(
                'user_id',
                Q.oneOf(uniq(rolesObject.map((entry) => entry.userId))),
              ),
            )
            .fetch()

          // OTIMIZAÇÃO G01: Criar Map com chave composta (backend_id + userId)
          const rolesMap = new Map(
            rolesObject.map((role) => [
              `${role.backend_id}_${role.userId}`,
              role,
            ]),
          )

          // OTIMIZAÇÃO G01: Criar Set de chaves existentes para lookup O(1)
          const existingRolesKeys = new Set(
            existingRolesEntries.map(
              (e) => `${e.backend_id}_${e._raw.user_id}`,
            ),
          )

          // OTIMIZAÇÃO G01: Filtrar usando Set.has() em vez de find()
          const rolesToInsert = rolesObject.filter(
            (role) =>
              !existingRolesKeys.has(`${role.backend_id}_${role.userId}`),
          )

          // OTIMIZAÇÃO G01: Separar updates e deletes usando Map O(1)
          const rolesToUpdate = []
          const rolesToDelete = []

          for (const entry of existingRolesEntries) {
            const key = `${entry.backend_id}_${entry._raw.user_id}`
            if (rolesMap.has(key)) {
              rolesToUpdate.push(entry)
            } else {
              rolesToDelete.push(entry)
            }
          }

          // Preparar operações de batch
          const rolesBatchOps = []

          // Updates usando Map O(1)
          // FIX: Forçar _status='synced' após prepareUpdate para evitar reenvio
          for (const entry of rolesToUpdate) {
            const key = `${entry.backend_id}_${entry._raw.user_id}`
            const roleData = rolesMap.get(key)
            rolesBatchOps.push(
              entry.prepareUpdate((record) => {
                fromDTO(AVAILABLE_DTO_MAPPINGS.ROLES, roleData, record, true)
                // CRÍTICO: Forçar status como synced
                record._raw._status = 'synced'
                record._raw._changed = ''
              }),
            )
          }

          // Inserts
          for (const roleData of rolesToInsert) {
            rolesBatchOps.push(
              rolesCollection.prepareCreate((newEntry) => {
                newEntry._raw.id = uuid()
                newEntry._raw._status = 'synced'
                fromDTO(AVAILABLE_DTO_MAPPINGS.ROLES, roleData, newEntry, true)
              }),
            )
          }

          // Deletes
          for (const entry of rolesToDelete) {
            rolesBatchOps.push(
              entry.prepareUpdate((record) => (record.isDeleted = true)),
            )
          }

          if (rolesBatchOps.length > 0) {
            await database.batch(...rolesBatchOps)
          }

          // OTIMIZAÇÃO G03: REMOVIDO prepareMarkAsSynced para entradas já synced
        }
      }
    })
  }
}

//TODO: Find a better way to define these parameters (besides hardcoding them)
// async function dbDomainValueIDLookup(
//   reference,
//   domainValue,
//   useParentID = false
// ) {
//   const collection = database.collections.get('domain_values')
//   const entries = await collection
//     .query(
//       useParentID
//         ? Q.on('domain_values', 'valor', reference)
//         : Q.on('domain_types', 'nome', reference),
//       Q.where('valor', domainValue)
//     )
//     .fetch()
//   if (entries && entries.length > 0) {
//     return entries[0].id
//   }
//   return null
// }

export async function refreshApplication() {
  let now = new Date()
  let lastRefresh = localStorage.getItem('Last Refresh')
  let cooldown = 60
  let loggedUserInfo = await getLoggedUser()

  if (lastRefresh) {
    let minuteDiference = Math.ceil(
      Math.abs(new Date(lastRefresh) - now) / 60000,
    )
    if (minuteDiference >= cooldown) {
      void recordEvent({
        level: 'info',
        category: 'sync',
        action: 'app_refresh_triggered',
        payload: { reason: 'manual_force', cooldownPassed: true },
      })
      await pull(loggedUserInfo, false, true).then((res) => {
        localStorage.setItem('Last Refresh', now)
        window.location.reload(true)
      })
      return
    } else {
      void recordEvent({
        level: 'info',
        category: 'sync',
        action: 'app_refresh_blocked_by_cooldown',
        payload: { minutesRemaining: cooldown - minuteDiference },
      })
      return `Você só pode forçar a sincronização a cada uma hora, faltam ${
        cooldown - minuteDiference
      } minutos.`
    }
  } else {
    void recordEvent({
      level: 'info',
      category: 'sync',
      action: 'app_refresh_triggered',
      payload: { reason: 'manual_force', cooldownPassed: true, firstRun: true },
    })
    await pull(loggedUserInfo, false, true).then((res) => {
      localStorage.setItem('Last Refresh', now)
      window.location.reload(true)
      return
    })
  }
}

export async function downloadBackup(text = null, selectedEstacaoDeMonta) {
  const startedAt = Date.now()
  recordEvent({
    level: 'info',
    category: 'outro',
    action: 'backup_started',
    payload: { hasEstacaoFilter: !!selectedEstacaoDeMonta },
  })

  try {
    // Dynamic import: JSZip (~30KB gzip) só é necessário aqui (backup por
    // interação do usuário), nunca no boot — retirado do chunk de entrypoint.
    // O chunk lazy entra no precache do service worker (InjectManifest), então
    // o backup continua funcionando offline após o SW instalar.
    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()
    const farmsRepository = new Repository(AVAILABLE_ENTITIES.FARMS)
    const downloadFarms =
      JSON.parse(localStorage.getItem('downloadFarms')) || []
    const { response: farms } = !!selectedEstacaoDeMonta
      ? await farmsRepository.getByParam(
          'estacaoMonta_id',
          selectedEstacaoDeMonta,
        )
      : await farmsRepository.get()

    const filtedFarms = farms.filter((farm) => {
      return downloadFarms.some((df) => df.id === farm.id)
    })

    const entitiesToFarm = [
      'fazendas',
      'retiros',
      'lotes',
      'd0s',
      'dns',
      'iatfs',
      'fazendaUsuarios',
    ]

    const ortherEntities = ['protocolos', 'touros', 'partidas', 'eventos']

    for (const farm of filtedFarms) {
      const fazendaFolder = zip.folder(
        `backup_fazenda_${farm.nome.replaceAll('/', '_')}`,
      )
      const ortherThings = zip.folder(`Outros`)

      for (const entitie of entitiesToFarm) {
        const payload = await mountPayloadDBToFarm(entitie, farm.id)
        const str = JSON.stringify(payload)
        fazendaFolder.file(`${entitie}.progerar`, str)
      }

      for (const ortherEntitie of ortherEntities) {
        const payload = await mountPayloadDB(ortherEntitie)
        const str = JSON.stringify(payload)
        ortherThings.file(`${ortherEntitie}.progerar`, str)
      }
    }

    const zipContent = await zip.generateAsync({ type: 'blob' })
    saveAs(zipContent, text || 'backup.zip')

    recordEvent({
      level: 'info',
      category: 'outro',
      action: 'backup_completed',
      payload: {
        farmsCount: filtedFarms.length,
        zipSizeKb: Math.round(zipContent.size / 1024),
        durationMs: Date.now() - startedAt,
      },
    })
  } catch (err) {
    recordError(err, {
      category: 'outro',
      action: 'backup_failed',
      payload: { durationMs: Date.now() - startedAt },
    })
    throw err
  }
}

export async function exportDb(value) {
  var payload = await mountPayloadDB(value)

  if (payload) {
    const str = JSON.stringify(payload)
    const bytes = new TextEncoder().encode(str)
    const blob = new Blob([bytes], {
      type: 'application/json;charset=utf-8',
    })

    const newHandle = await window.showSaveFilePicker()
    const writableStream = await newHandle.createWritable()
    await writableStream.write(blob)
    await writableStream.close()
  } else {
    alert('Não há dados a ser exportados')
  }
}

export async function mountPayloadDBToFarm(value, farmId) {
  let loggedUser = await (await getLoggedUser()).userId
  var contentPayload = []
  var entries = []

  const corralsRepository = new Repository(AVAILABLE_ENTITIES.CORRALS)
  const { response: corrals } = await corralsRepository.getByParam(
    'farm_id',
    farmId,
  )
  const batchesRepository = new Repository(AVAILABLE_ENTITIES.BATCHES)
  const { response: batches } = await batchesRepository.getByParam(
    'corral_id',
    Q.oneOf(corrals.map((corral) => corral.id)),
  )

  const collection = database.collections.get(SYNC_OBJECT_TO_TABLE[value])
  const entityEntry = await database.collections
    .get('domain_values')
    .query(
      Q.on('domain_types', 'nome', 'entidade'),
      Q.where('valor', normalizeEntityName(value)),
    )
    .fetch()

  const valuesToFarm = Q.or(
    Q.where('id', Q.eq(farmId)),
    Q.where('farm_id', Q.eq(farmId)),
    Q.where('id', Q.oneOf(corrals.map((corral) => corral.id))),
    Q.where('corral_id', Q.oneOf(corrals.map((corral) => corral.id))),
    Q.where('id', Q.oneOf(batches.map((batch) => batch.id))),
    Q.where('batch_id', Q.oneOf(batches.map((batch) => batch.id))),
  )
  const deletedIsNotTrue = Q.where('isDeleted', Q.notEq(true))
  const combinedQuery = Q.and(valuesToFarm, deletedIsNotTrue)

  const result = await collection.query(combinedQuery).fetch()

  // G007: chunk() sem size produzia chunks de 1 — o processamento já era
  // serial. Iteração direta explicita isso sem mudar comportamento.
  for (const entry of result) {
    const verb = 'POST'

    contentPayload.push(await createPayloadFor(entry, value, true, verb))

    entries.push(entry)
  }

  if (contentPayload.length > 0) {
    let payload = {
      tipoId: entityEntry[0].id,
      loggedUser,
      contents: contentPayload,
    }
    return payload
  } else {
    return null
  }
}

export async function mountPayloadDB(value) {
  let loggedUser = await (await getLoggedUser()).userId
  var contentPayload = []
  var entries = []

  const entityEntry = await database.collections
    .get('domain_values')
    .query(
      Q.on('domain_types', 'nome', 'entidade'),
      Q.where('valor', normalizeEntityName(value)),
    )
    .fetch()

  const result = await database.collections
    .get(SYNC_OBJECT_TO_TABLE[value])
    .query(Q.where('id', Q.like('%-%')))
    .fetch()

  // G007: chunk() sem size produzia chunks de 1 — o processamento já era
  // serial. Iteração direta explicita isso sem mudar comportamento.
  for (const entry of result) {
    const verb = 'POST'

    contentPayload.push(await createPayloadFor(entry, value, true, verb))

    entries.push(entry)
  }

  if (contentPayload.length > 0) {
    let payload = {
      // entityEntry pode estar vazia para entidades sem registro em domain_values
      // (ex.: 'eventos' — tabela local-only de observabilidade). O backup ZIP
      // ainda funciona porque o atendimento só lê os contents.
      tipoId: entityEntry[0]?.id ?? null,
      loggedUser,
      contents: contentPayload,
    }
    return payload
  } else {
    return null
  }
}

// ============================================================================
// FUNÇÕES DE DIAGNÓSTICO E MONITORAMENTO
// Exportadas para uso em debugging e troubleshooting
// ============================================================================

/**
 * Obtém estatísticas completas do sistema de sincronização
 * Útil para debugging e monitoramento
 */
export { getSyncStats, exportSyncLogs }

/**
 * Obtém o status atual do sistema de sync
 */
export async function getSyncStatus() {
  const queueStats = await syncQueue.getStats()
  return {
    isSyncing,
    isForcedSync,
    currentStatus,
    enhancedSyncEnabled: USE_ENHANCED_SYNC,
    queue: queueStats,
    tokenManager: tokenManager.getStatus(),
  }
}

/**
 * Gera relatório de diagnóstico completo
 */
export async function generateDiagnosticReport() {
  console.log('[SYNC] Gerando relatório de diagnóstico...')
  const syncStatus = await getSyncStatus()
  const report = {
    generatedAt: new Date().toISOString(),
    syncStatus,
    logs: syncLogger.generateReport(),
    localStorage: {
      downloadFarms: JSON.parse(localStorage.getItem('downloadFarms') || '[]')
        .length,
      lastSyncPull: localStorage.getItem('lastSyncPull'),
      lastRefresh: localStorage.getItem('Last Refresh'),
    },
  }

  console.log('=== SYNC DIAGNOSTIC REPORT ===')
  console.log(JSON.stringify(report, null, 2))

  return report
}
